import { BalanceData, GemPackData, GemPackScreen, OfferScreen, gemPacksAllowed, priceCents, purchasesAllowed, Wallet } from "@highmarch/domain";
import { Clock, Logger } from "./ports";
import type { Deals } from "./deals";
import { allStoreProducts } from "./dealsResolve";
import { DealGrant, DealsContext } from "./dealsTypes";
import { ProductId, Store, StoreProduct, StoreTransaction } from "./store";

/**
 * Real-money gem packs through the `Store` port (ADR 0013, owner decision D9). The catalogue is data
 * (shop.json `gemPacks`: product id → gems); the adapters are WP4's (a sandbox store on the web, StoreKit 2 in the
 * iOS shell). The rules:
 * - each verified transaction is granted once, by `transactionId` (the wallet keeps the ids), then saved, then
 *   finished — so a crash anywhere re-delivers it through `unfinished()` and the second delivery grants nothing;
 * - a revoked (refunded) transaction takes back what is still unspent of its gems, once;
 * - a transaction for a product this build does not know is left unfinished (a later build may know it);
 * - purchases start only in the hub (the never list), never in a level or from the end screen.
 * Deals products (docs/SHOP.md §4.2) take the same path: `buyDeal` records the purchase intent (Deals.prepare) and
 * persists it before the store is asked; a verified Deals transaction is granted by `Deals.grant`, saved, then
 * finished; a refund takes back the unspent gems it paid (the wallet's purchase record), once.
 */

/** `notSaved`: this copy of the game cannot save (another tab has the save): left unfinished for the one that can. */
export type GrantOutcome = "granted" | "duplicate" | "revoked" | "unknownProduct" | "invalid" | "notSaved";

/** What `buyDeal` did: the grant (items, VIP points, banked gear) when the store paid at once. */
export type DealBuyOutcome =
  | { readonly status: "granted"; readonly grant: Extract<DealGrant, { status: "granted" }> }
  | { readonly status: "pending" | "cancelled" | "duplicate" }
  | { readonly status: "refused" | "failed"; readonly reason: string };

/** What `buyProduct` did (a product a feature grants itself: the season pass). */
export type ProductBuyOutcome =
  | { readonly status: "granted" }
  | { readonly status: "pending" | "cancelled" | "duplicate" }
  | { readonly status: "refused" | "failed"; readonly reason: string };

/**
 * A real-money product line a feature grants itself (the season pass, MP-22), plugged in with `addHandler`: its
 * transactions take the same path as Deals — checked and saved before the store is asked, granted once by transaction
 * id, saved, then finished; a refund calls `revoke`. Its products are in `allStoreProducts` (the store's price list).
 */
export interface ProductHandler {
  knowsProduct(productId: ProductId): boolean;
  /** Whether it can be bought now (hub only, on sale, not owned): true, or why not. */
  prepare(productId: ProductId, now: number, screen: OfferScreen): true | string;
  /** Grants a verified transaction once; the caller persists, then finishes it. */
  grant(t: StoreTransaction, now: number): "granted" | "duplicate" | "unknownProduct";
  /** A refunded transaction: take back what it gave, where it can. */
  revoke?(transactionId: string): unknown;
}

export type BuyOutcome =
  | { readonly status: "granted"; readonly gems: number }
  | { readonly status: "pending" | "cancelled" | "duplicate" }
  | { readonly status: "refused" | "failed"; readonly reason: string };

/** The refusal when the save cannot be written here (Economy's refusal code too): nothing is sold that would be lost. */
export const NOT_SAVING_HERE = "not saving here";
/** More than this many of one pack in one transaction is not a real purchase (StoreKit's own cap is 10). */
const MAX_QUANTITY = 10;
const MS_PER_SECOND = 1000;

export interface PurchaseDeps {
  readonly store: Store;
  readonly packs: readonly GemPackData[];
  /** The live wallet (the catalog's; re-read on every call, as an import can replace it). */
  wallet(): Wallet;
  /**
   * Stores the save (the wallet section with it). A promise resolves once the save is durable: the transaction is
   * finished with the store only then, so a crash in between re-delivers it rather than losing paid gems.
   */
  persist(): void | Promise<void>;
  /** Whether the save can be written now (web: false in a tab that lost the save to another tab). Default: yes. */
  canPersist?(): boolean;
  readonly clock: Clock;
  readonly log?: Logger;
  /** The live Deals use cases (the catalog's, re-read on every call); absent: Deals products are left unfinished. */
  deals?(): Deals | null;
  /** The balance bundle (every product the store sells: gem packs and Deals); absent: gem packs only. */
  readonly balance?: BalanceData;
}

export class PurchaseService {
  /** Deals grants made while this copy of the game runs, by transaction id (what `buyDeal` reports). */
  private readonly dealGrants = new Map<string, Extract<DealGrant, { status: "granted" }>>();
  private front: { known: boolean; code: string | null } = { known: false, code: null };
  /** Product lines features grant themselves (`addHandler`). */
  private readonly handlers: ProductHandler[] = [];

  constructor(private readonly d: PurchaseDeps) {}

  pack(id: ProductId): GemPackData | undefined { return this.d.packs.find((p) => p.id === id); }

  /** The first-purchase bonus (percent of a gem pack; 0 without the balance bundle). */
  private get firstBonusPercent(): number { return this.d.balance?.commerce.shop.firstPurchaseBonusPercent ?? 0; }

  /** Whether `packId`'s next purchase pays the first-purchase bonus (the pack's ×2 ribbon). */
  firstBonus(packId: ProductId): boolean { return this.firstBonusPercent > 0 && !this.d.wallet().boughtBefore(packId); }

  /** The gem packs the store sells, in catalogue order, with the store's own localised price. */
  async products(): Promise<{ pack: GemPackData; product: StoreProduct }[]> {
    const known = await this.d.store.products(this.d.packs.map((p) => p.id));
    return this.d.packs.flatMap((pack) => { const product = known.find((k) => k.id === pack.id); return product ? [{ pack, product }] : []; });
  }

  /**
   * Every product the store sells (gem packs, then Deals: `allStoreProducts`) with the store's own localised price, by
   * product id — the Deals screen's price buttons. Products the store does not know are left out.
   */
  async storeProducts(): Promise<Map<ProductId, StoreProduct>> {
    const ids = this.d.balance ? allStoreProducts(this.d.balance).map((p) => p.id) : this.d.packs.map((p) => p.id);
    return new Map((await this.d.store.products(ids)).map((p) => [p.id, p]));
  }

  /** The storefront's country (alpha-3) once asked (`refreshStorefront`); null when unknown. */
  async refreshStorefront(): Promise<string | null> {
    let code: string | null = null;
    try { code = (await this.d.store.storefront?.()) ?? null; } catch { code = null; }
    this.front = { known: true, code: typeof code === "string" && /^[A-Z]{3}$/.test(code) ? code : null };
    return this.front.code;
  }

  /**
   * Where the Deals screen is shown (docs/SHOP.md §4.3): paid offers are allowed with a store — at once with the
   * sandbox store, and with StoreKit once the storefront was asked (so a random offer never flashes up in a storefront
   * that hides it).
   */
  dealsContext(): DealsContext {
    return { paidAllowed: this.d.store.kind === "sandbox" || this.front.known, storefront: this.front.code };
  }

  /**
   * Buys a Deals offer (docs/SHOP.md §4.2): checks it and records the intent (Deals.prepare), persists that, then asks
   * the store; a purchase that ends without a transaction (cancelled, failed) drops its intent. A pending one (Ask to
   * Buy) keeps it and arrives later through `listen`.
   */
  async buyDeal(offerId: string, ctx: DealsContext, screen: OfferScreen = "hub"): Promise<DealBuyOutcome> {
    if (!purchasesAllowed(screen)) return { status: "refused", reason: "not here" };
    const deals = this.d.deals?.() ?? null;
    if (!deals) return { status: "refused", reason: "no such product" };
    if (!this.canPersist()) return { status: "refused", reason: NOT_SAVING_HERE };
    if (!(await this.d.store.canPay())) return { status: "refused", reason: "purchases are turned off on this device" };
    const intent = deals.prepare(offerId, this.d.clock.utcNowSeconds(), ctx, screen);
    if (typeof intent === "string") return { status: "refused", reason: intent };
    await this.d.persist();
    const r = await this.d.store.purchase(intent.product);
    if (r.status === "pending") return { status: "pending" };
    if (r.status !== "purchased") {
      deals.state.dropIntent(intent);
      await this.d.persist();
      return r.status === "failed" ? { status: "failed", reason: r.reason } : { status: r.status };
    }
    const out = await this.handle(r.transaction);
    const grant = this.dealGrants.get(r.transaction.transactionId);
    if (out === "granted" && grant) return { status: "granted", grant };
    if (out === "duplicate") return { status: "duplicate" };
    return { status: "failed", reason: out };
  }

  /** The grant a Deals transaction got in this run (null: not granted here, or not a Deals product). */
  dealGrant(transactionId: string): Extract<DealGrant, { status: "granted" }> | null { return this.dealGrants.get(transactionId) ?? null; }

  /**
   * Buys a gem pack: refused outside the hub (and the Starfall Wyrm's choice: `gemPacksAllowed`, the one in-battle top-up)
   * or for an unknown pack; a pending purchase arrives later through `listen`.
   */
  async buy(id: ProductId, screen: GemPackScreen): Promise<BuyOutcome> {
    if (!gemPacksAllowed(screen)) return { status: "refused", reason: "not here" };
    const pack = this.pack(id);
    if (!pack) return { status: "refused", reason: "no such product" };
    if (!this.canPersist()) return { status: "refused", reason: NOT_SAVING_HERE };
    if (!(await this.d.store.canPay())) return { status: "refused", reason: "purchases are turned off on this device" };
    const r = await this.d.store.purchase(id);
    if (r.status !== "purchased") return r.status === "failed" ? { status: "failed", reason: r.reason } : { status: r.status };
    const out = await this.handle(r.transaction);
    if (out === "granted") return { status: "granted", gems: this.d.wallet().purchase(r.transaction.transactionId)?.gems ?? 0 };
    if (out === "duplicate") return { status: "duplicate" };
    return { status: "failed", reason: out };
  }

  /** Plugs in a feature's product line (the season pass); returns the unplug. */
  addHandler(h: ProductHandler): () => void {
    this.handlers.push(h);
    return () => { const i = this.handlers.indexOf(h); if (i >= 0) this.handlers.splice(i, 1); };
  }

  /** The handler that grants `productId`, or null (a gem pack, a Deals offer, unknown). */
  handlerFor(productId: ProductId): ProductHandler | null { return this.handlers.find((h) => h.knowsProduct(productId)) ?? null; }

  /** Buys a handler's product (hub only): its check first, then the store; a pending one arrives later through `listen`. */
  async buyProduct(productId: ProductId, screen: OfferScreen = "hub"): Promise<ProductBuyOutcome> {
    if (!purchasesAllowed(screen)) return { status: "refused", reason: "not here" };
    const h = this.handlerFor(productId);
    if (!h) return { status: "refused", reason: "no such product" };
    if (!this.canPersist()) return { status: "refused", reason: NOT_SAVING_HERE };
    if (!(await this.d.store.canPay())) return { status: "refused", reason: "purchases are turned off on this device" };
    const ok = h.prepare(productId, this.d.clock.utcNowSeconds(), screen);
    if (ok !== true) return { status: "refused", reason: ok };
    const r = await this.d.store.purchase(productId);
    if (r.status === "pending") return { status: "pending" };
    if (r.status !== "purchased") return r.status === "failed" ? { status: "failed", reason: r.reason } : { status: r.status };
    const out = await this.handle(r.transaction);
    if (out === "granted" || out === "duplicate") return { status: out };
    return { status: "failed", reason: out };
  }

  /** Grants (or revokes) one verified transaction, once, then finishes it with the store. */
  async handle(t: StoreTransaction): Promise<GrantOutcome> {
    if (!validTransaction(t)) { this.d.log?.log("warning", `store: ignored a malformed transaction`); return "invalid"; }
    if (!this.canPersist()) { this.d.log?.log("warning", `store: ${t.transactionId} left unfinished, the save is not writable here`); return "notSaved"; }
    const wallet = this.d.wallet(), now = this.d.clock.utcNowSeconds();
    if (t.revokedAtMs !== undefined) {
      wallet.noteSignedDate(Math.floor(t.revokedAtMs / MS_PER_SECOND));
      const taken = wallet.revokePurchase(t.transactionId, t.productId, now);
      this.d.deals?.()?.revoked(t.transactionId);   // a card's remaining days, a fund's milestones (MP-13)
      this.handlerFor(t.productId)?.revoke?.(t.transactionId);
      await this.d.persist();
      await this.d.store.finish(t.transactionId);
      this.d.log?.log("info", `store: ${t.productId} refunded, ${taken} gems taken back`);
      return "revoked";
    }
    const deals = this.d.deals?.() ?? null;
    if (deals?.knowsProduct(t.productId)) return this.handleDeal(deals, t, now);
    const handler = this.handlerFor(t.productId);
    if (handler) return this.handleProduct(handler, t);
    const pack = this.pack(t.productId);
    if (!pack) { this.d.log?.log("warning", `store: unknown product ${t.productId} left unfinished`); return "unknownProduct"; }
    if (wallet.purchase(t.transactionId) !== null) { await this.d.store.finish(t.transactionId); return "duplicate"; }
    // owner 2026-10-21: a pack's first purchase pays firstPurchaseBonusPercent more gems (double), once per pack
    const bonus = wallet.boughtBefore(t.productId) ? 0 : Math.floor((pack.gems * this.firstBonusPercent) / 100);
    if (!wallet.grantPurchase(t.transactionId, t.productId, pack.gems * t.quantity + bonus, Math.floor(t.purchasedAtMs / MS_PER_SECOND))) return "invalid";
    // Every purchase gives VIP XP (docs/SHOP.md §8.1: vip.json xpPerDollar, the gem pack's bubble), once with the gems it pays.
    deals?.notePurchase(packCents(pack), t.quantity);
    await this.d.persist();
    await this.d.store.finish(t.transactionId);
    return "granted";
  }

  /** A verified Deals transaction: granted once (Deals.grant records it in the wallet too), saved, then finished. */
  private async handleDeal(deals: Deals, t: StoreTransaction, now: number): Promise<GrantOutcome> {
    const g = deals.grant(t, now);
    if (g.status !== "granted") {
      if (g.status === "unknownProduct") { this.d.log?.log("warning", `store: unknown product ${t.productId} left unfinished`); return "unknownProduct"; }
      await this.d.store.finish(t.transactionId);
      return "duplicate";
    }
    this.dealGrants.set(t.transactionId, g);
    await this.d.persist();
    await this.d.store.finish(t.transactionId);
    return "granted";
  }

  /** A verified transaction of a handler's product: granted once (the handler records it), saved, then finished. */
  private async handleProduct(h: ProductHandler, t: StoreTransaction): Promise<GrantOutcome> {
    const g = h.grant(t, this.d.clock.utcNowSeconds());
    if (g === "unknownProduct") { this.d.log?.log("warning", `store: unknown product ${t.productId} left unfinished`); return "unknownProduct"; }
    if (g === "granted") await this.d.persist();
    await this.d.store.finish(t.transactionId);
    return g;
  }

  /** At launch: grants what the store delivered but the game never finished (a crash between paying and granting). */
  async deliverUnfinished(): Promise<GrantOutcome[]> {
    const out: GrantOutcome[] = [];
    for (const t of await this.d.store.unfinished()) out.push(await this.handle(t));
    return out;
  }

  /**
   * Settings → Restore purchases. Gems are consumables: the store never returns one that was finished (StoreKit's
   * restore lists only entitlements), so what this can bring back is a payment that never finished, the same as at
   * launch, plus anything the store's restore still lists. Rejects when the store cannot be reached (sign-in cancelled).
   */
  async restore(): Promise<GrantOutcome[]> {
    const out = await this.deliverUnfinished();
    for (const t of await this.d.store.restore()) out.push(await this.handle(t));
    return out;
  }

  private canPersist(): boolean { return this.d.canPersist?.() ?? true; }

  /** Transactions arriving outside `buy` (Ask to Buy approved later, a refund): handled as they come. Returns the unsubscribe. */
  listen(onDone?: (t: StoreTransaction, outcome: GrantOutcome) => void): () => void {
    return this.d.store.onTransaction((t) => { void this.handle(t).then((o) => onDone?.(t, o)); });
  }
}

/** A gem pack's US reference price in cents (its VIP points); 0 (no points) for a label off the price ladder. */
function packCents(pack: GemPackData): number {
  try { return priceCents(pack.referencePrice); } catch { return 0; }
}

function validTransaction(t: StoreTransaction): boolean {
  return typeof t === "object" && t !== null && typeof t.transactionId === "string" && t.transactionId.length > 0 && typeof t.productId === "string"
    && Number.isInteger(t.quantity) && t.quantity >= 1 && t.quantity <= MAX_QUANTITY && Number.isFinite(t.purchasedAtMs)
    && (t.revokedAtMs === undefined || Number.isFinite(t.revokedAtMs));
}
